import { useMemo } from 'react'
import { searchConfig } from '@cocoonspace/shared/config/search-config'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import {
	Calendar,
	type CalendarProps,
} from '@cocoonspace/ui/components/calendar'
import { Card } from '@cocoonspace/ui/components/card'
import {
	Select,
	SelectContentWithoutPortal,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@cocoonspace/ui/components/select'
import { useIsMobile } from '@cocoonspace/ui/hooks/use-mobile'
import { cn } from '@cocoonspace/ui/lib/utils'
import { Trans, useLingui } from '@lingui/react/macro'
import { fr } from 'date-fns/locale/fr'
import { LogInIcon, LogOutIcon, type LucideIcon } from 'lucide-react'

import { DayPicker } from '~/domains/search/components/filters/timeslot-filter/day-picker'
import { getDayValueAsArray, type Timeslot } from './index'

const locales = {
	fr,
}

const styles: CalendarProps['classNames'] = {
	months:
		'md:chop-x chop-slate-200 chop-x-offset-5 grid grid-cols-1 gap-10 md:grid-cols-2',
}

export const TimePicker = ({
	value,
	Icon,
	onChange,
}: {
	value?: string
	Icon: LucideIcon
	onChange: (value: string) => void
}) => {
	const options = useMemo(() => {
		return searchConfig.timeSlots.filter((timeSlot) => {
			return (
				dateTimeUtils.utils.getTimeAsMinutes(searchConfig.timeSlot.min) <=
					dateTimeUtils.utils.getTimeAsMinutes(timeSlot) &&
				dateTimeUtils.utils.getTimeAsMinutes(searchConfig.timeSlot.max) >=
					dateTimeUtils.utils.getTimeAsMinutes(timeSlot)
			)
		})
	}, [])

	return (
		<Select
			onValueChange={onChange}
			value={value}
		>
			<SelectTrigger className='gap-2'>
				<Icon
					className='text-primary shrink-0'
					size={16}
				/>

				<SelectValue />
			</SelectTrigger>

			<SelectContentWithoutPortal className='w-[80px]'>
				{options.map((option) => (
					<SelectItem
						key={option}
						value={option}
					>
						{option}
					</SelectItem>
				))}
			</SelectContentWithoutPortal>
		</Select>
	)
}

const defaultTimeDiff = 60

export const getEndTime = (newStart: string, value: Timeslot) => {
	if (!value.start && !value.end)
		return dateTimeUtils.utils.addDurationToTime(
			newStart,
			defaultTimeDiff,
			'minute',
		)

	const valueTimeDiff =
		(!!value.start &&
			!!value.end &&
			dateTimeUtils.utils
				.parseTime(value.end)
				.diff(dateTimeUtils.utils.parseTime(value.start), 'minute')) ||
		defaultTimeDiff

	const newEndTime =
		!!value.end && newStart < value.end
			? value.end
			: dateTimeUtils.utils.addDurationToTime(newStart, valueTimeDiff, 'minute')

	// * return the new end time if it's between the min and max time slot, else return the max end time
	return newEndTime > searchConfig.timeSlot.min &&
		newEndTime < searchConfig.timeSlots[searchConfig.timeSlots.length - 1]
		? newEndTime
		: searchConfig.timeSlot.max
}

export const getStartTime = (newEnd: string, value: Timeslot) =>
	!!value.start && newEnd <= value.start ? '' : value.start

export const SingleDate = ({
	className,
	value,
	onChange,
}: {
	isComplete: boolean
	className?: string
	value: Timeslot
	onChange: (value: Timeslot) => void
}) => {
	const { t } = useLingui()

	return (
		<div
			className={cn(
				'chop-slate-200 chop-x-offset-5 chop-y-offset-5 max-md:chop-y md:chop-x grid grid-cols-1 gap-10 md:grid-cols-2',
				className,
			)}
		>
			<DayPicker
				className='w-full [&_.rdp-table]:mx-auto'
				onChange={(day) => onChange({ ...value, day: [day] })}
				value={value.day ? getDayValueAsArray(value.day)?.[0] : undefined}
			/>

			<div className='flex flex-col'>
				<div className='mb-4 text-center'>{t`À quelle heure ?`}</div>

				<div className='flex flex-row items-center justify-center gap-8 md:flex-col md:gap-4'>
					<TimePicker
						Icon={LogInIcon}
						onChange={(start) => {
							const newEndTime = getEndTime(start, value)

							onChange({
								...value,
								end: newEndTime,
								start,
							})
						}}
						value={value.start || undefined}
					/>

					<TimePicker
						Icon={LogOutIcon}
						onChange={(end) => {
							const newStartTime = getStartTime(end, value)

							onChange({
								...value,
								end,
								start: newStartTime,
							})
						}}
						value={value.end || undefined}
					/>
				</div>

				<Card className='mt-6 bg-slate-200/30 p-2 text-xs'>
					<Trans>
						🌈 Soyez prévoyant et économisez <strong>jusqu'à 30%</strong> sur
						votre réservation en anticipant votre évènement.
					</Trans>
				</Card>
			</div>
		</div>
	)
}

export const MultiDate = ({
	value,
	onChange,
	className,
}: {
	isComplete: boolean
	className?: string
	value: Timeslot
	onChange: (value: Timeslot) => void
}) => {
	const { i18n } = useLingui()
	const isMobile = useIsMobile()

	return (
		<Calendar
			className={cn('w-full [&_.rdp-table]:mx-auto', className)}
			classNames={styles}
			fromDate={new Date()}
			locale={locales[i18n.locale as 'fr']}
			mode='multiple'
			numberOfMonths={isMobile ? 1 : 2}
			onSelect={(days) => {
				const day = days?.map((day) =>
					dateTimeUtils(day).customFormat('dateTech'),
				)

				onChange({
					day: day || null,
					end: null,
					start: null,
				})
			}}
			selected={
				value.day
					? getDayValueAsArray(value.day)?.map((d) => dateTimeUtils(d).toDate())
					: undefined
			}
			toDate={dateTimeUtils().add(1, 'year').toDate()}
		/>
	)
}
